// Small DOM helpers shared by the shell and every view. Part of the shell API in
// CONTRACT.md — views may import { el, fmtAge, laneColor } from here, and nothing else.

/**
 * Create an element with attributes/props and children in one call.
 * `el("div", { class: "panel", onclick: fn }, [child1, "text", child2])`
 */
export function el(tag, props = {}, children = []) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props || {})) {
    if (value == null || value === false) continue;
    if (key.startsWith("on") && typeof value === "function") {
      node.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (key === "class" || key === "className") {
      node.className = value;
    } else if (key === "dataset") {
      Object.assign(node.dataset, value);
    } else if (key in node && key !== "list") {
      try {
        node[key] = value;
      } catch {
        node.setAttribute(key, value);
      }
    } else {
      node.setAttribute(key, value);
    }
  }
  const kids = Array.isArray(children) ? children : [children];
  for (const child of kids) {
    if (child == null || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

/**
 * Format an ISO timestamp (or Date) as a short relative age, e.g. "3m", "2h", "5d".
 * Returns "—" for a missing/invalid input.
 */
export function fmtAge(when) {
  if (!when) return "—";
  const then = when instanceof Date ? when : new Date(when);
  const ms = Date.now() - then.getTime();
  if (!Number.isFinite(ms)) return "—";
  if (ms < 0) return "now";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

const LANES = new Set(["circ", "cat", "ros", "txt", "trial", "pub", "ops", "core"]);

/**
 * Resolve a lane key ("circ", "cat", ...) to its CSS custom property value.
 * Falls back to --ops for an unrecognised lane rather than throwing, since a lane
 * list is data from Firestore and can grow independently of this file.
 */
export function laneColor(lane) {
  const key = LANES.has(lane) ? lane : "ops";
  return getComputedStyle(document.documentElement).getPropertyValue(`--${key}`).trim();
}

/** Turns off the phone/tablet typing helpers on a field whose text must match what the operator reads (iPad capitalises
 *  the first letter, autocorrect rewrites names). Attributes, not properties: `spellcheck={false}` is skipped by el(). */
export function noTypingHelpers(input) {
  input.setAttribute("autocapitalize", "off");
  input.setAttribute("autocorrect", "off");
  input.setAttribute("autocomplete", "off");
  input.setAttribute("spellcheck", "false");
  return input;
}
